import client from '@/api/client'
import { PRODUCT_TYPE_FILTERS } from '@/components/FilterPage/constants'
import filtersQuery from '@/api/gql/filter-page/query/filter-page-filters.gql'
import paginatedItemsQuery from '@/api/gql/filter-page/query/filter-page-paginated-items.gql'
import promoCardsQuery from '@/api/gql/filter-page/query/filter-page-promocards.gql'
import subPagesQuery from '@/api/gql/filter-page/query/filter-page-subpages.gql'
import filtersPageQuery from '@/api/gql/filter-page/query/filter-page.gql'

export const fetchPaginatedItems = variables => {
  return client
    .query({
      query: paginatedItemsQuery,
      variables,
    })
    .then(({ data: { filterPage } }) => ({
      articlesPageBottom: filterPage.articlesPageBottom,
      explodeVariants: filterPage.explodeVariants,
      ...filterPage.paginatedItems,
    }))
}

export const fetchPromoCards = variables => {
  return client
    .query({
      query: promoCardsQuery,
      variables,
    })
    .then(({ data: { promoCards } }) => ({ promoCards }))
}

export const fetchSubPages = variables => {
  return client
    .query({
      query: subPagesQuery,
      variables,
    })
    .then(({ data: { filterPage } }) => ({ subPages: filterPage.subPages }))
}

export const fetchFilterPageData = variables => {
  return client
    .query({
      query: filtersPageQuery,
      variables,
    })
    .then(({ data: { filterPage } }) => ({ ...filterPage }))
}

export const fetchFilters = variables => {
  return client
    .query({
      query: filtersQuery,
      variables,
    })
    .then(({ data: { filterPage } }) => ({ filters: filterPage.filters }))
}

export const addValuesToFilters = (filters, newFilter, newValues) => {
  const hasValidValues = newValues.every(value => newFilter.values.includes(value))
  if (hasValidValues) {
    const curIndex = filters.findIndex(filter => filter.id === newFilter.id)
    if (curIndex > -1) {
      filters[curIndex].values.push(...newValues)
    } else {
      filters.push({
        ...newFilter,
        values: newValues,
      })
    }
  }
}

export const formatSubpages = subPages => {
  // from [filterValues]: { 'gender': ['men'], 'product': ['shoes'] }
  // to [filterMatches]: ['men', 'shoes']
  return subPages.map(subPage => ({
    ...subPage,
    filterMatches: Object.values(subPage.filterValues).flat(),
  }))
}

export const formatFilters = filters => {
  return filters.map(filter => ({
    ...filter,
    // set [type] to 'checkbox' as default
    type: filter.type || 'checkbox',
    // copy [values] to [options]
    options: filter.values,
    // from [values]: [{ 'mens': 'Men', 'womens': 'Women' }]
    // to [values]: ['mens', 'womens']
    values: filter.values.map(filter => filter.value),
  }))
}

export const formatItems = (items = [], hasExplodeVariants = false) => {
  const newItems = []
  let explodedId = 0

  /**
   * Items
   */
  items.forEach(item => {
    // - productGroup
    item.productGroup = item.variants[0]?.spreeProduct.groupSlug || ''
    // - item price = Number
    item.price = +item.price

    /**
     * Exploded variant
     * - create a copy of each variant as a new item
     */
    if (hasExplodeVariants && item?.variants?.length > 0) {
      item.variants.forEach(variant => {
        const newItem = {
          ...JSON.parse(JSON.stringify(item)),
          id: `${item.id}-${explodedId}`,
          variants: [JSON.parse(JSON.stringify(variant))],
        }

        newItems.push(newItem)
        explodedId++
      })
    } else {
      newItems.push(item)
    }
  })

  return newItems
}

export const filterIsGender = filter => filter.id === 'gender'

export const filterIsType = filter => Object.values(PRODUCT_TYPE_FILTERS).includes(filter.id)

export const scrollToFiltersBar = () => {
  const filtersBar = document.querySelector('.filter-page__bar')
  const header = document.querySelector('.site-header')

  if (filtersBar && header) {
    const filterBarTop = filtersBar.offsetTop
    const scrolled = window.scrollY

    // if before of after, scroll
    if (scrolled !== filterBarTop) {
      window.scrollTo({
        top: filterBarTop,
        behavior: 'smooth',
      })
    }
  }
}

/**
 * @return the number of products per row for the current window width
 * based on front/components/_shared/Cards/card-basic.scss
 */
export const getColumnsByWindow = () => {
  const windowWidth = window.innerWidth
  let columns = 2

  if (windowWidth > 3200) columns = 7
  else if (windowWidth > 2600) columns = 6
  else if (windowWidth > 2200) columns = 5
  else if (windowWidth > 1770) columns = 4
  else if (windowWidth > 1280) columns = 3

  return columns
}

/**
 * Hides exceeding items
 * IF the last row does not have all the products to fill the row
 * Depending on the window size
 *
 * @return nothing! => change the array in place
 */
export const setOnlyFullRows = productList => {
  const columns = getColumnsByWindow()
  const rest = productList.length % columns

  if (rest > 0) productList.splice(-rest)
}

export const newPageInfo = () => ({
  hasNextPage: false,
  hasPreviousPage: false,
  startCursor: '',
  endCursor: '',
  totalCount: 0,
})
